import React from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../../lib/theme';
import { type V1Admin } from '../../../../lib/v1';
import { assigneeNames } from '../../PbDashboard/helpers';
import { TaskInProgressBadge } from '../../PbDashboard/TaskStatus';
import { TaskPriorityBadge, highPriorityRowStyle } from '../../PbDashboard/TaskPriority';
import { fmtDateTime } from './helpers';
import { ActionBtn } from './ActionBtn';
import type { TaskRow } from './types';

export function TaskRowCard({
  task, dark, onToggle, onEdit, onDelete, admins,
}: {
  task: TaskRow;
  dark: boolean;
  onToggle: (e: React.MouseEvent) => void;
  onEdit: () => void;
  onDelete: () => void;
  /** Batch-12: Admin-Liste, um zugewiesene Bearbeiter anzuzeigen. */
  admins?: V1Admin[];
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const done = task.status === 1;
  const due = task.zieldatum ?? task.datum ?? task.erinnerung;
  // Batch-12: Bearbeiter-Namen (wie im Kundendashboard).
  const names = assigneeNames(task.bearbeiter, admins ?? []);
  return (
    <div style={{
      padding: '11px 14px', borderBottom: `1px solid ${t.borderSoft}`,
      display: 'flex', alignItems: 'flex-start', gap: 10,
      opacity: done ? 0.55 : 1,
      ...highPriorityRowStyle(task, dark),
    }}>
      {/* status toggle */}
      <button
        type="button"
        title={done ? tr('pflege.detail.dashboard.task_reopen') : tr('pflege.detail.dashboard.task_done')}
        onClick={onToggle}
        style={{
          flexShrink: 0, width: 20, height: 20, borderRadius: '50%', marginTop: 2,
          border: `1.5px solid ${done ? '#12b76a' : t.border}`,
          background: done ? '#12b76a' : 'transparent',
          color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >{done ? '✓' : ''}</button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 500, color: t.heading, textDecoration: done ? 'line-through' : 'none', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>{task.titel ?? tr('pflege.tasks.no_title')}</span>
          <TaskPriorityBadge task={task} />
          <TaskInProgressBadge status={task.status} />
        </div>
        {task.beschreibung ? (
          <div style={{ fontSize: 12.5, color: t.muted, marginTop: 2, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
            {task.beschreibung}
          </div>
        ) : null}
        {due && (
          <div style={{ marginTop: 5, fontSize: 11.5, color: BRAND, fontWeight: 500 }}>
            <span style={{ background: BRAND_LIGHT, padding: '2px 8px', borderRadius: 999 }}>
              {tr('pflege.tasks.due', { date: fmtDateTime(due) })}
            </span>
          </div>
        )}
        {names && (
          <div style={{ marginTop: 4, fontSize: 11.5, color: t.muted }}>
            {tr('pflege.detail.dashboard.task_assignee_prefix')} {names}
          </div>
        )}
        {/* Batch-13 Paket E — Personen-Chip: nur im Konto-Modus vorhanden
            (task.user_name), fehlt bei kontoweiten Einträgen ohne Personenbezug. */}
        {task.user_name && (
          <div style={{ marginTop: 4, fontSize: 11, color: t.muted }}>
            <span style={{ background: t.hover, border: `1px solid ${t.border}`, padding: '2px 8px', borderRadius: 999 }}>
              {tr('pflege.tasks.person_chip', { name: task.user_name })}
            </span>
          </div>
        )}
      </div>
      {/* actions */}
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <ActionBtn onClick={onEdit} title={tr('common.action.edit')} dark={dark}>✎</ActionBtn>
        <ActionBtn onClick={onDelete} title={tr('common.action.delete')} dark={dark} danger>✕</ActionBtn>
      </div>
    </div>
  );
}
